import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/* ============================================================
   Landing — the public homepage (logged-out "/").

   Same x.ai monochrome system as the app: pure black, hairlines,
   oversized type. Motion is Apple-style and dependency-free:
   - scroll reveals (one IntersectionObserver over [data-rv])
   - hero parallax that drifts + fades as you scroll
   - cursor-tracked spotlight cards (radial glow follows the mouse)
   - a sticky scroll story (pinned headline, panels scroll past)
   All of it collapses gracefully under prefers-reduced-motion.

   Copy is 90% value (what coaching feels like with it) and 10%
   feature proof (the compact capability strip near the end).
============================================================ */

/* ---------- ambient video layer ----------
   Monochrome loops generated to match the black/hairline theme. Always muted,
   looped, inline; decorative only (aria-hidden) and skipped entirely under
   prefers-reduced-motion so the page stays calm AND saves the bandwidth. */
// Skip the decorative video layers entirely on phones, Save-Data connections,
// or under reduced-motion: ~4 MB of background loops is wasted bandwidth there
// and a likely cause of "won't load" on slow mobile links. These are aria-hidden
// ambient layers, so rendering nothing is visually safe (the dark theme shows
// through). Evaluated once at module level — viewport class doesn't change mid-session.
const skipAmbientVideo: boolean =
  typeof window !== 'undefined' &&
  (window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    window.matchMedia('(max-width: 768px)').matches ||
    // navigator.connection is non-standard; guard access.
    (navigator as unknown as { connection?: { saveData?: boolean } }).connection?.saveData === true);

const Vid: React.FC<{ src: string; className?: string; style?: React.CSSProperties }> = ({ src, className, style }) => {
  if (skipAmbientVideo) return null;
  return (
    <video
      className={className}
      style={style}
      src={src}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      aria-hidden
      tabIndex={-1}
    />
  );
};

/* ---------- tiny mock primitives (stylized product, not screenshots) ---------- */

const MockRow: React.FC<{ name: string; sets: string }> = ({ name, sets }) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-fit-line pb-1.5">
    <span className="text-[12px] font-semibold">{name}</span>
    <span className="text-[11px] text-fit-text-secondary tabular-nums">{sets}</span>
  </div>
);

const MockPlanner: React.FC = () => (
  <div className="lp-mock card glass" aria-hidden>
    <p className="mono-label mb-3">Week of Jun 8</p>
    <div className="grid grid-cols-7 gap-1.5">
      {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
        <div key={i} className="rounded-md border border-fit-line p-1.5 min-h-[72px] flex flex-col gap-1">
          <span className="text-[9px] text-fit-muted font-semibold">{d}</span>
          {[0, 2, 4].includes(i) && (
            <span className="rounded bg-fit-bg-elevated border border-fit-line px-1 py-0.5 text-[8px] font-semibold truncate">
              {i === 0 ? 'Lower' : i === 2 ? 'Upper' : 'Full body'}
            </span>
          )}
          {i === 5 && <span className="rounded border border-fit-line px-1 py-0.5 text-[8px] text-fit-muted truncate">Check-in</span>}
        </div>
      ))}
    </div>
    <div className="flex items-center gap-2 mt-3">
      <span className="rounded-full bg-white text-fit-ink px-2 py-0.5 text-[9px] font-bold">Copy last week ⟳</span>
      <span className="rounded-full border border-fit-line px-2 py-0.5 text-[9px] text-fit-text-secondary">Apply program ↓</span>
    </div>
  </div>
);

const MockLog: React.FC = () => (
  <div className="lp-mock card glass" aria-hidden>
    <p className="mono-label mb-3">Tue · Lower body</p>
    <div className="flex flex-col gap-2">
      <MockRow name="Back squat" sets="135×5 · 155×5 · 175×3" />
      <MockRow name="Bb RDL" sets="115×10 · 115×10" />
      <MockRow name="Split squat" sets="30×8/leg · 30×8/leg" />
      <MockRow name="Pallof press" sets="30s · 30s · 30s" />
    </div>
    <p className="text-[10px] text-fit-muted mt-3">last wk · 165×3 — beat it by 10 lb</p>
  </div>
);

// The trend clip IS panel 03's message (check-ins becoming a rising line), so it
// stands in as that panel's product visual, framed like the other mocks.
const MockTrend: React.FC<{ motionOK: boolean }> = ({ motionOK }) =>
  motionOK ? (
    <div className="lp-mock lp-vidcard" aria-hidden>
      <Vid src="/video/trend.mp4" className="lp-vidcard-vid" />
    </div>
  ) : (
    <div className="lp-mock card glass" aria-hidden>
      <p className="mono-label mb-3">Progress · Weight (lb)</p>
      <svg viewBox="0 0 200 60" className="w-full" aria-hidden>
        <path d="M4,50 L40,44 L80,38 L120,30 L160,20 L196,10" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" />
        {[4, 40, 80, 120, 160, 196].map((x, i) => (
          <circle key={i} cx={x} cy={50 - i * 8} r="2" fill="white" />
        ))}
      </svg>
    </div>
  );

/* ---------- game-grade FX (canvas + rAF, all dependency-free) ----------
   Each effect respects prefers-reduced-motion and only runs while on screen,
   so the page stays calm and cheap. Same monochrome system as everything else.
   - ParticleField: drifting depth-motes, or a "thinking" node network
   - Scramble: a messy cell decoding into a clean value (the import story)
   - TypeOut: the Advisor reply typing in like a terminal
   - CountUp: stat reveals that tick up when scrolled into view */

const prefersStill = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const ParticleField: React.FC<{ variant?: 'drift' | 'network' }> = ({ variant = 'drift' }) => {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (prefersStill()) return;
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0, h = 0, raf = 0, running = false;
    interface P { x: number; y: number; z: number; vx: number; vy: number; r: number }
    let pts: P[] = [];
    const build = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width; h = rect.height;
      canvas.width = Math.max(1, w * dpr); canvas.height = Math.max(1, h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const base = variant === 'network' ? 42 : 60;
      const count = Math.round(base * Math.min(1.5, Math.max(0.5, (w * h) / (1280 * 700))));
      pts = Array.from({ length: count }, () => {
        const z = Math.random();
        const sp = variant === 'network' ? 0.16 : 0.1;
        return {
          x: Math.random() * w, y: Math.random() * h, z,
          vx: (Math.random() - 0.5) * sp * (0.4 + z),
          vy: (Math.random() - 0.5) * sp * (0.4 + z) - (variant === 'drift' ? 0.04 + z * 0.05 : 0),
          r: variant === 'network' ? 1 + z * 1.1 : 0.5 + z * 1.7,
        };
      });
    };
    const frame = () => {
      ctx.clearRect(0, 0, w, h);
      for (const pt of pts) {
        pt.x += pt.vx; pt.y += pt.vy;
        if (pt.x < -24) pt.x = w + 24; else if (pt.x > w + 24) pt.x = -24;
        if (pt.y < -24) pt.y = h + 24; else if (pt.y > h + 24) pt.y = -24;
      }
      if (variant === 'network') {
        const max = 116;
        for (let i = 0; i < pts.length; i++) {
          for (let j = i + 1; j < pts.length; j++) {
            const a = pts[i], b = pts[j];
            const dx = a.x - b.x, dy = a.y - b.y, d2 = dx * dx + dy * dy;
            if (d2 < max * max) {
              ctx.strokeStyle = `rgba(255,255,255,${(1 - Math.sqrt(d2) / max) * 0.14})`;
              ctx.lineWidth = 1;
              ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
            }
          }
        }
      }
      for (const pt of pts) {
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, pt.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${(variant === 'network' ? 0.12 : 0.05) + pt.z * 0.3})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(frame);
    };
    build();
    const onResize = () => build();
    window.addEventListener('resize', onResize);
    const io = new IntersectionObserver((es) => {
      const vis = es.some((e) => e.isIntersecting);
      if (vis && !running) { running = true; raf = requestAnimationFrame(frame); }
      else if (!vis && running) { running = false; cancelAnimationFrame(raf); }
    }, { threshold: 0 });
    io.observe(canvas);
    return () => { cancelAnimationFrame(raf); io.disconnect(); window.removeEventListener('resize', onResize); };
  }, [variant]);
  return <canvas ref={ref} className="lp-particles" aria-hidden />;
};

const CountUp: React.FC<{ to: number; from?: number; decimals?: number; suffix?: string; prefix?: string; dur?: number }> =
  ({ to, from = 0, decimals = 0, suffix = '', prefix = '', dur = 1300 }) => {
    const ref = useRef<HTMLSpanElement>(null);
    const [v, setV] = useState(from);
    useEffect(() => {
      const el = ref.current;
      if (!el) return;
      if (prefersStill()) { setV(to); return; }
      let raf = 0, fired = false;
      const io = new IntersectionObserver((es) => {
        if (!es[0].isIntersecting || fired) return;
        fired = true; io.disconnect();
        let start = 0;
        const tick = (t: number) => {
          if (!start) start = t;
          const prog = Math.min(1, (t - start) / dur);
          const eased = 1 - Math.pow(1 - prog, 3);
          setV(from + (to - from) * eased);
          if (prog < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      }, { threshold: 0.5 });
      io.observe(el);
      return () => { cancelAnimationFrame(raf); io.disconnect(); };
    }, [to, from, dur]);
    return <span ref={ref}>{prefix}{v.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}</span>;
  };

const TypeOut: React.FC<{ text: string; speed?: number }> = ({ text, speed = 16 }) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const [shown, setShown] = useState('');
  const [phase, setPhase] = useState<'wait' | 'think' | 'type' | 'done'>('wait');
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersStill()) { setShown(text); setPhase('done'); return; }
    const timers: number[] = [];
    let armed = true;
    const io = new IntersectionObserver((es) => {
      if (!es[0].isIntersecting || !armed) return;
      armed = false; io.disconnect();
      setPhase('think');
      timers.push(window.setTimeout(() => {
        setPhase('type');
        let i = 0;
        const id = window.setInterval(() => {
          i += 1; setShown(text.slice(0, i));
          if (i >= text.length) { clearInterval(id); setPhase('done'); }
        }, speed);
        timers.push(id);
      }, 850));
    }, { threshold: 0.55 });
    io.observe(el);
    return () => { timers.forEach((t) => { clearTimeout(t); clearInterval(t); }); io.disconnect(); };
  }, [text, speed]);
  return (
    <p ref={ref} className="lp-chat-text">
      {phase === 'wait' || phase === 'think'
        ? <span className="lp-typedots"><i /><i /><i /></span>
        : <>{shown}{phase === 'type' && <span className="lp-caret" />}</>}
    </p>
  );
};

const SCRAMBLE_GLYPHS = '#@$%&/=+*0123456789ABCDEF';
const Scramble: React.FC<{ text: string; delay?: number; className?: string }> = ({ text, delay = 0, className }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [out, setOut] = useState(() => text.replace(/[^ ]/g, '·'));
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersStill()) { setOut(text); return; }
    let raf = 0, fired = false;
    const io = new IntersectionObserver((es) => {
      if (!es[0].isIntersecting || fired) return;
      fired = true; io.disconnect();
      const begin = performance.now() + delay;
      const dur = 820;
      const tick = (t: number) => {
        const prog = Math.max(0, Math.min(1, (t - begin) / dur));
        let str = '';
        for (let i = 0; i < text.length; i++) {
          const ch = text[i];
          if (ch === ' ') { str += ' '; continue; }
          const at = i / text.length;
          if (prog > at + 0.12) str += ch;
          else if (prog > at - 0.12) str += SCRAMBLE_GLYPHS[(Math.random() * SCRAMBLE_GLYPHS.length) | 0];
          else str += '·';
        }
        setOut(str);
        if (prog < 1) raf = requestAnimationFrame(tick); else setOut(text);
      };
      raf = requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(el);
    return () => { cancelAnimationFrame(raf); io.disconnect(); };
  }, [text, delay]);
  return <span ref={ref} className={className}>{out}</span>;
};

/* ---------- mocks for the three flagship sections ---------- */

const IMPORT_ROWS: [string, string, string][] = [
  ['Jordan Hale', 'jordan@mail.com', 'Lower · Tue'],
  ['Sam Patel', 'sam@mail.com', 'Upper · Wed'],
  ['Taylor Kim', 'taylor@mail.com', 'Full body · Fri'],
  ['Morgan Lee', 'morgan@mail.com', 'Check-in due'],
];

const MockImport: React.FC = () => (
  <div className="lp-mock lp-import card glass" aria-hidden>
    <div className="lp-import-head">
      <span className="lp-spin" />
      <span className="mono-label">Reading your spreadsheet…</span>
    </div>
    <div className="lp-import-rows">
      {IMPORT_ROWS.map((r, i) => (
        <div key={i} className="lp-import-row">
          <Scramble text={r[0]} delay={i * 130} className="lp-import-name" />
          <Scramble text={r[1]} delay={i * 130 + 60} className="lp-import-meta" />
          <Scramble text={r[2]} delay={i * 130 + 120} className="lp-import-tag" />
        </div>
      ))}
    </div>
    <p className="lp-import-done">✓ Roster ready · 4 clients mapped</p>
  </div>
);

const MockAdvisor: React.FC = () => (
  <div className="lp-mock lp-chat card glass" aria-hidden>
    <p className="lp-chat-q">Which schedule got him the best results?</p>
    <div className="lp-chat-a">
      <p className="lp-chat-tag">✦ Advisor</p>
      <TypeOut text="His biggest jumps clustered in the 3-day weeks — bench 135 → 185, deadlift 230 → 250 — while body fat held near 12%. The training-frequency research lines up with it." />
      <p className="lp-chat-src">Sources · Sports Medicine · PubMed</p>
    </div>
  </div>
);

const MockCase: React.FC = () => (
  <div className="lp-mock lp-case card glass" aria-hidden>
    <p className="mono-label">Case study · <CountUp to={24} /> months</p>
    <h4 className="lp-case-h">Eric's transformation</h4>
    <div className="lp-case-photos">
      <div className="lp-case-photo"><span>Before</span></div>
      <div className="lp-case-photo lp-case-photo--after"><span>After</span></div>
    </div>
    <div className="lp-case-stats">
      <div><strong><CountUp from={17.5} to={9.3} decimals={1} suffix="%" /></strong><span>body fat</span></div>
      <div><strong><CountUp from={145} to={151} /></strong><span>lb lean</span></div>
      <div><strong><CountUp to={255} /></strong><span>sessions</span></div>
    </div>
    <p className="lp-case-foot">FITFLEX · fitflex.club</p>
  </div>
);

/* ---------- the page ---------- */

const FEATURES: [string, string][] = [
  ['AI import', 'any spreadsheet, mapped for you'],
  ['Full history import', 'a client’s whole log, one click'],
  ['✦ Advisor', 'chat about any client, with sources'],
  ['Case study builder', 'results into share-ready proof'],
  ['Weekly planner', 'every client, one board'],
  ['Live session logger', 'sets typed like a whiteboard'],
  ['Training log + trends', 'weight · body fat · muscle'],
  ['Transformation photos', 'private — before & after'],
  ['Calendar sync', 'pull your Google Calendar in'],
  ['Leads pipeline', 'follow-up nudges built in'],
  ['Exercise library', 'search-as-you-type prefill'],
  ['Client logins', 'invite when they’re ready'],
];

const Landing: React.FC = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  // Evaluated once: when the visitor prefers reduced motion we skip rendering
  // every <video> (static fallbacks instead) — calmer page, ~4 MB saved.
  const [motionOK] = useState(() => !window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  // One observer animates every [data-rv] element in, once, with a
  // per-element stagger read from data-rv (its delay in ms).
  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>('[data-rv]'));
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const el = e.target as HTMLElement;
          el.style.transitionDelay = `${el.dataset.rv || 0}ms`;
          el.classList.add('rv-in');
          io.unobserve(el);
        }),
      { threshold: 0.18, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Hero parallax: content drifts up slower than the page and fades out.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        const el = heroRef.current;
        if (!el) return;
        el.style.transform = `translateY(${y * 0.28}px)`;
        el.style.opacity = String(Math.max(0, 1 - y / 640));
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Spotlight cards: the radial glow tracks the cursor via CSS vars.
  const spot = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - r.left}px`);
    el.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  return (
    <div className="lp">
      {/* ---------- nav ---------- */}
      <nav className="lp-nav">
        <span className="app-header-brand text-[19px]">FITFLEX</span>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-[13px] text-fit-text-secondary hover:text-white font-semibold no-underline">Sign in</Link>
          <Link to="/login" className="btn btn-primary btn-sm no-underline">Get started</Link>
        </div>
      </nav>

      {/* ---------- hero ---------- */}
      <header className="lp-hero">
        {motionOK && (
          <div className="lp-hero-vid" aria-hidden>
            <Vid src="/video/hero-chalk.mp4" className="lp-vid" />
            <div className="lp-vid-scrim" />
          </div>
        )}
        <div className="lp-hero-glow" aria-hidden />
        {motionOK && <ParticleField variant="drift" />}
        <div ref={heroRef} className="lp-hero-inner">
          <p className="mono-label rv" data-rv="0">For coaches who care</p>
          <h1 className="lp-h1 rv" data-rv="80">
            Know every client
            <br />
            like your <em>only</em> client.
          </h1>
          <p className="lp-sub rv" data-rv="200">
            Fitflex turns the hours you lose to spreadsheets into hours spent coaching.
            Plan a week in minutes, log sets while the bar is still warm, and never let
            a client — or a lead — drift.
          </p>
          <div className="flex items-center justify-center gap-3 rv" data-rv="320">
            <Link to="/login" className="btn btn-primary btn-lg no-underline">Start coaching free</Link>
            <a href="#story" className="btn btn-glass btn-lg no-underline">See how it works</a>
          </div>
          <span className="lp-pill lp-pill--hero rv" data-rv="420">Early access · free during beta</span>
        </div>
      </header>

      {/* ---------- giant statement over the wireframe athlete ---------- */}
      <section className="lp-band">
        {motionOK && (
          <>
            <Vid src="/video/wireframe.mp4" className="lp-vid lp-band-vid" />
            <div className="lp-vid-scrim" />
          </>
        )}
        <h2 className="lp-statement rv" data-rv="0">
          <span className="lp-dim">Less admin.</span> More coaching.
        </h2>
      </section>

      {/* ---------- spotlight value cards ---------- */}
      <section className="lp-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              k: 'Plan',
              h: 'A week of programming in one coffee.',
              p: 'Your whole roster on one calm board. Copy last week forward, drop in a proven program, tweak the numbers — done before the espresso cools.',
            },
            {
              k: 'Coach',
              h: 'Eyes on your client, not on paperwork.',
              p: 'Log sets the way you would scribble them — 135×5, 30s, Blue band — with last week’s numbers sitting right there to beat.',
            },
            {
              k: 'Grow',
              h: 'The business side runs itself.',
              p: 'Leads get follow-up nudges. Sessions land in your real calendar. New clients accept a private invite and choose their password. You just coach.',
            },
          ].map((c, i) => (
            <div key={c.k} className="lp-spot rv" data-rv={i * 120} onMouseMove={spot}>
              <p className="mono-label mb-3">{c.k}</p>
              <h3 className="text-[22px] font-bold tracking-tight mb-2">{c.h}</h3>
              <p className="text-[14px] text-fit-text-secondary leading-relaxed">{c.p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- sticky scroll story ---------- */}
      <section className="lp-section" id="story">
        <div className="lp-story">
          <div className="lp-story-pin">
            <p className="mono-label mb-3">A week with Fitflex</p>
            <h2 className="lp-h2">
              From Monday's plan
              <br />
              to Friday's proof.
            </h2>
            <p className="text-[15px] text-fit-text-secondary leading-relaxed mt-4" style={{ maxWidth: 380 }}>
              One tool carries the whole week — the plan, the sessions as they actually
              happened, and the trend line that shows your client it's working.
            </p>
          </div>
          <div className="lp-story-panels">
            {[
              {
                label: '01 · Plan',
                h: 'Program the week on one board',
                p: 'Every client, every day, drag-of-a-thought fast. Last week is one click away from being this week.',
                mock: <MockPlanner />,
              },
              {
                label: '02 · Train',
                h: 'Log it live, mid-session',
                p: 'Reach for your exercise library as you go. Sets read like your whiteboard, with last week beside them daring you to add ten pounds.',
                mock: <MockLog />,
              },
              {
                label: '03 · Grow',
                h: 'Watch the story add up',
                p: 'Weekly check-ins become trend lines. Two years of training fits in one scroll — proof your coaching works, ready to show.',
                mock: <MockTrend motionOK={motionOK} />,
              },
            ].map((s, i) => (
              <div key={i} className="lp-panel rv" data-rv="0">
                <p className="mono-label mb-2">{s.label}</p>
                <h3 className="text-[24px] font-bold tracking-tight mb-2">{s.h}</h3>
                <p className="text-[14px] text-fit-text-secondary leading-relaxed mb-5" style={{ maxWidth: 420 }}>{s.p}</p>
                {s.mock}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- flagship features (the differentiators) ---------- */}
      <section className="lp-section">
        <p className="mono-label mb-2 rv" data-rv="0">Why coaches don’t go back</p>

        {/* A — switch in minutes */}
        <div className="lp-feature">
          <div className="lp-feature-copy rv" data-rv="0">
            <p className="mono-label mb-3">Switch in minutes</p>
            <h3 className="lp-feature-h">Bring your whole world with you.</h3>
            <p className="lp-feature-p">
              Years of spreadsheets and training logs? Drop them in. The AI reads any layout —
              even a messy two-year workout journal — and a client’s entire history lands in one
              click. No re-typing. No starting over.
            </p>
          </div>
          <div className="lp-feature-art rv" data-rv="140"><MockImport /></div>
        </div>

        {/* B — the Advisor (with a thinking node-network behind it) */}
        <div className="lp-feature lp-feature--flip lp-feature--brainy">
          {motionOK && <ParticleField variant="network" />}
          <div className="lp-feature-copy rv" data-rv="0">
            <span className="lp-pill">✦ Premium · free during beta</span>
            <h3 className="lp-feature-h">An assistant that’s read every rep.</h3>
            <p className="lp-feature-p">
              Ask anything about a client — “which schedule got the best results?” — and get a
              straight answer, grounded in their real data. Want the science behind it? It reads
              the research and cites its sources.
            </p>
          </div>
          <div className="lp-feature-art rv" data-rv="140"><MockAdvisor /></div>
        </div>

        {/* C — case study builder */}
        <div className="lp-feature">
          <div className="lp-feature-copy rv" data-rv="0">
            <p className="mono-label mb-3">Results that recruit</p>
            <h3 className="lp-feature-h">Their wins, doing your marketing.</h3>
            <p className="lp-feature-p">
              Two transformation photos and a tap turn a client’s real numbers into a share-ready
              story — fat down, lifts up, months in. Post it. Watch your next client find you.
            </p>
          </div>
          <div className="lp-feature-art rv" data-rv="140"><MockCase /></div>
        </div>
      </section>

      {/* ---------- proof strip (the 10%) ---------- */}
      <section className="lp-section">
        <p className="mono-label mb-5 rv" data-rv="0">Everything it does — at a glance</p>
        <div className="lp-proof">
          {FEATURES.map(([h, p], i) => (
            <div key={h} className="lp-proof-cell rv" data-rv={i * 50}>
              <p className="text-[14px] font-semibold">{h}</p>
              <p className="text-[12px] text-fit-muted mt-0.5">{p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- kettlebell divider band ---------- */}
      {motionOK && (
        <section className="lp-band lp-band-quiet" aria-hidden>
          <Vid src="/video/kettlebell.mp4" className="lp-vid lp-band-vid" />
          <div className="lp-vid-scrim" />
          <p className="mono-label rv" data-rv="0" style={{ position: 'relative' }}>Built for the work</p>
        </section>
      )}

      {/* ---------- closing CTA ---------- */}
      <section className="lp-section lp-cta">
        {motionOK && (
          <div className="lp-cta-vid" aria-hidden>
            <Vid src="/video/ambient-lines.mp4" className="lp-vid" />
            <div className="lp-vid-scrim" />
          </div>
        )}
        <h2 className="lp-statement rv" data-rv="0">
          You changed their lives.
          <br />
          <span className="lp-dim">Let's return the favor.</span>
        </h2>
        <div className="flex justify-center mt-8 rv" data-rv="160">
          <Link to="/login" className="btn btn-primary btn-lg no-underline">Start coaching with Fitflex</Link>
        </div>
        <p className="text-[12px] text-fit-muted text-center mt-4 rv" data-rv="240">
          Early access — free during beta · Clients join by invite
        </p>
      </section>

      <footer className="lp-footer">
        <span className="mono-label">FITFLEX</span>
        <div className="lp-footer-links">
          <Link to="/privacy" className="no-underline">Privacy</Link>
          <Link to="/terms" className="no-underline">Terms</Link>
          <Link to="/transparency" className="no-underline">AI &amp; Data</Link>
        </div>
      </footer>

      {/* ---------- landing-only styles ---------- */}
      <style>{`
        .lp { min-height: 100vh; background: var(--bg-primary); overflow-x: clip; }
        .lp a.no-underline { text-decoration: none; }

        .lp-nav {
          position: sticky; top: 0; z-index: 40;
          display: flex; align-items: center; justify-content: space-between;
          padding: 14px clamp(16px, 5vw, 48px);
          background: rgba(0,0,0,0.6);
          backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
          border-bottom: 1px solid var(--line);
        }

        .lp-hero {
          position: relative;
          min-height: 92vh;
          display: grid; place-items: center;
          padding: 48px clamp(16px, 5vw, 48px);
          overflow: hidden;
        }
        .lp-hero-glow {
          position: absolute; inset: 0; pointer-events: none;
          background:
            radial-gradient(900px 500px at 50% -10%, rgba(255,255,255,0.10), transparent 60%),
            radial-gradient(700px 400px at 80% 110%, rgba(255,255,255,0.04), transparent 60%);
        }
        .lp-hero-inner {
          position: relative; /* paints above the absolute video/glow layers */
          display: flex; flex-direction: column; align-items: center; gap: 22px;
          text-align: center; max-width: 880px; will-change: transform, opacity;
        }
        .lp-h1 {
          font-size: clamp(40px, 7.2vw, 84px);
          font-weight: 800; letter-spacing: -0.045em; line-height: 0.98;
        }
        .lp-h1 em { font-style: italic; color: var(--text-secondary); }
        .lp-sub {
          font-size: clamp(15px, 1.6vw, 19px);
          color: var(--text-secondary); line-height: 1.6; max-width: 620px;
        }
        .lp-h2 {
          font-size: clamp(30px, 4vw, 46px);
          font-weight: 800; letter-spacing: -0.04em; line-height: 1.04;
        }

        .lp-section { padding: clamp(56px, 9vw, 120px) clamp(16px, 5vw, 48px); max-width: 1200px; margin: 0 auto; }
        .lp-statement {
          font-size: clamp(34px, 6vw, 72px);
          font-weight: 800; letter-spacing: -0.045em; line-height: 1.02; text-align: center;
        }
        .lp-dim { color: var(--text-muted); }

        /* spotlight cards — the glow follows the cursor */
        .lp-spot {
          position: relative;
          border: 1px solid var(--line);
          border-radius: var(--radius-lg);
          background: var(--bg-secondary);
          padding: 26px 24px;
          transition: transform 0.35s cubic-bezier(0.16,1,0.3,1), border-color 0.25s ease;
          overflow: hidden;
        }
        .lp-spot::before {
          content: '';
          position: absolute; inset: 0; pointer-events: none;
          background: radial-gradient(420px circle at var(--mx, 50%) var(--my, 0%), rgba(255,255,255,0.08), transparent 45%);
          opacity: 0; transition: opacity 0.3s ease;
        }
        .lp-spot:hover { transform: translateY(-5px); border-color: var(--line-strong); }
        .lp-spot:hover::before { opacity: 1; }
        .lp-spot > * { position: relative; }

        /* sticky story */
        .lp-story { display: grid; grid-template-columns: minmax(0, 5fr) minmax(0, 7fr); gap: clamp(24px, 4vw, 64px); }
        .lp-story-pin { position: sticky; top: 110px; align-self: start; height: fit-content; }
        .lp-story-panels { display: flex; flex-direction: column; gap: clamp(48px, 7vw, 110px); }
        .lp-panel { border-top: 1px solid var(--line); padding-top: 28px; }
        .lp-mock { max-width: 460px; }
        @media (max-width: 768px) {
          .lp-story { grid-template-columns: 1fr; }
          .lp-story-pin { position: static; }
        }

        /* proof strip — hairline grid */
        .lp-proof {
          display: grid; grid-template-columns: repeat(4, 1fr);
          border-top: 1px solid var(--line); border-left: 1px solid var(--line);
        }
        .lp-proof-cell {
          border-right: 1px solid var(--line); border-bottom: 1px solid var(--line);
          padding: 18px 16px;
        }
        @media (max-width: 900px) { .lp-proof { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 480px) { .lp-proof { grid-template-columns: 1fr; } }

        /* ---------- ambient video layers ---------- */
        .lp-vid {
          position: absolute; inset: 0; width: 100%; height: 100%;
          object-fit: cover; pointer-events: none;
        }
        /* vignette to black on every edge so the 16:9 frame melts into the page */
        .lp-vid-scrim {
          position: absolute; inset: 0; pointer-events: none;
          background:
            radial-gradient(ellipse 90% 75% at 50% 50%, transparent 40%, rgba(0,0,0,0.92) 100%),
            linear-gradient(to bottom, #000 0%, transparent 14%, transparent 86%, #000 100%);
        }
        .lp-hero-vid { position: absolute; inset: 0; opacity: 0.38; }
        /* extra center veil so the headline block always sits on near-black */
        .lp-hero-vid .lp-vid-scrim {
          background:
            radial-gradient(ellipse 62% 52% at 50% 48%, rgba(0,0,0,0.55), transparent 75%),
            radial-gradient(ellipse 90% 75% at 50% 50%, transparent 40%, rgba(0,0,0,0.92) 100%),
            linear-gradient(to bottom, #000 0%, transparent 14%, transparent 86%, #000 100%);
        }
        .lp-hero .mono-label { color: var(--text-secondary); }
        .lp-band {
          position: relative; overflow: hidden;
          display: grid; place-items: center;
          min-height: 64vh;
          padding: clamp(56px, 9vw, 120px) clamp(16px, 5vw, 48px);
        }
        .lp-band .lp-band-vid { opacity: 0.55; }
        .lp-band .lp-statement { position: relative; text-shadow: 0 2px 24px rgba(0,0,0,0.8); }
        .lp-band-quiet { min-height: 44vh; }
        .lp-band-quiet .lp-band-vid { opacity: 0.6; }
        .lp-vidcard {
          border: 1px solid var(--line); border-radius: var(--radius-lg);
          overflow: hidden; aspect-ratio: 16 / 9; position: relative;
          background: var(--bg-secondary);
        }
        .lp-vidcard-vid { width: 100%; height: 100%; object-fit: cover; display: block; }

        .lp-cta { text-align: center; position: relative; overflow: hidden; }
        .lp-cta-vid { position: absolute; inset: 0; opacity: 0.3; }
        .lp-cta > *:not(.lp-cta-vid) { position: relative; }
        .lp-footer {
          display: flex; align-items: center; justify-content: space-between;
          padding: 22px clamp(16px, 5vw, 48px);
          border-top: 1px solid var(--line);
        }
        .lp-footer-links { display: flex; gap: 18px; }
        .lp-footer-links a { font-size: 12px; color: var(--text-muted); }
        .lp-footer-links a:hover { color: var(--text-secondary); }

        /* reveals — Apple-style ease-out rise */
        .rv { opacity: 0; transform: translateY(26px) scale(0.985); }
        .rv.rv-in {
          opacity: 1; transform: none;
          transition: opacity 0.9s cubic-bezier(0.16,1,0.3,1), transform 0.9s cubic-bezier(0.16,1,0.3,1);
        }

        /* ---------- game-grade FX surfaces ---------- */
        .lp-particles { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; }
        .lp-hero .lp-particles { opacity: 0.5; z-index: 0; }

        .lp-pill {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
          color: var(--text-secondary);
          border: 1px solid var(--line-strong); border-radius: 999px;
          padding: 5px 12px;
        }
        .lp-pill--hero { margin-top: 4px; }

        /* flagship feature rows — alternating copy / art */
        .lp-feature {
          display: grid; grid-template-columns: minmax(0,1fr) minmax(0,1fr);
          gap: clamp(28px, 5vw, 72px); align-items: center;
          padding: clamp(40px, 6vw, 88px) 0;
          border-top: 1px solid var(--line);
        }
        .lp-feature:first-of-type { border-top: none; }
        .lp-feature--flip .lp-feature-copy { order: 2; }
        .lp-feature--flip .lp-feature-art { order: 1; }
        .lp-feature--brainy {
          position: relative; isolation: isolate; overflow: hidden;
          border: 1px solid var(--line); border-radius: var(--radius-lg);
          padding-left: clamp(20px, 4vw, 48px); padding-right: clamp(20px, 4vw, 48px);
          margin: 16px 0;
          background: radial-gradient(120% 120% at 50% 0%, rgba(255,255,255,0.045), transparent 62%);
        }
        .lp-feature--brainy .lp-particles { opacity: 0.85; z-index: 0; }
        .lp-feature--brainy > .lp-feature-copy, .lp-feature--brainy > .lp-feature-art { position: relative; z-index: 1; }
        .lp-feature-copy .lp-pill { margin-bottom: 14px; }
        .lp-feature-h { font-size: clamp(26px, 3.4vw, 40px); font-weight: 800; letter-spacing: -0.035em; line-height: 1.05; margin-bottom: 14px; }
        .lp-feature-p { font-size: clamp(14px, 1.5vw, 17px); color: var(--text-secondary); line-height: 1.65; max-width: 440px; }
        @media (max-width: 820px) {
          .lp-feature { grid-template-columns: 1fr; }
          .lp-feature--flip .lp-feature-copy, .lp-feature--flip .lp-feature-art { order: 0; }
        }

        /* import decode mock */
        .lp-import-head { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; }
        .lp-spin { width: 12px; height: 12px; border-radius: 50%; border: 1.5px solid var(--line-strong); border-top-color: #fff; animation: lp-spin 0.8s linear infinite; }
        @keyframes lp-spin { to { transform: rotate(360deg); } }
        .lp-import-rows { display: flex; flex-direction: column; gap: 9px; }
        .lp-import-row { display: grid; grid-template-columns: 1.1fr 1.3fr 0.9fr; gap: 10px; align-items: baseline; border-bottom: 1px solid var(--line); padding-bottom: 9px; font-variant-numeric: tabular-nums; }
        .lp-import-name { font-size: 13px; font-weight: 700; }
        .lp-import-meta { font-size: 11px; color: var(--text-secondary); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; overflow: hidden; white-space: nowrap; }
        .lp-import-tag { font-size: 10px; color: var(--text-muted); text-align: right; }
        .lp-import-done { font-size: 12px; color: var(--text-secondary); margin-top: 14px; }

        /* advisor chat mock */
        .lp-chat { display: flex; flex-direction: column; gap: 12px; }
        .lp-chat-q { align-self: flex-end; max-width: 82%; background: rgba(255,255,255,0.1); border-radius: 14px 14px 4px 14px; padding: 10px 14px; font-size: 13px; font-weight: 600; }
        .lp-chat-a { align-self: flex-start; max-width: 94%; border: 1px solid var(--line); border-radius: 14px 14px 14px 4px; padding: 12px 14px; background: var(--bg-secondary); }
        .lp-chat-tag { font-size: 9px; text-transform: uppercase; letter-spacing: 0.12em; color: var(--text-muted); margin-bottom: 6px; }
        .lp-chat-text { font-size: 13px; line-height: 1.6; color: var(--text-secondary); min-height: 4.8em; }
        .lp-chat-src { font-size: 10px; color: var(--text-muted); margin-top: 8px; }
        .lp-typedots { display: inline-flex; gap: 4px; padding: 4px 0; }
        .lp-typedots i { width: 5px; height: 5px; border-radius: 50%; background: var(--text-secondary); animation: lp-dot 1s infinite ease-in-out; }
        .lp-typedots i:nth-child(2) { animation-delay: 0.15s; }
        .lp-typedots i:nth-child(3) { animation-delay: 0.3s; }
        @keyframes lp-dot { 0%, 80%, 100% { opacity: 0.25; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-3px); } }
        .lp-caret { display: inline-block; width: 7px; height: 1.05em; background: #fff; margin-left: 2px; vertical-align: text-bottom; animation: lp-blink 1s steps(1) infinite; }
        @keyframes lp-blink { 50% { opacity: 0; } }

        /* case-study mock */
        .lp-case-h { font-size: 20px; font-weight: 800; letter-spacing: -0.03em; margin: 6px 0 14px; }
        .lp-case-photos { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 16px; }
        .lp-case-photo { position: relative; aspect-ratio: 3 / 4; border-radius: 10px; overflow: hidden; display: grid; place-items: end start; background: linear-gradient(160deg, #18181d, #0b0b0d); }
        .lp-case-photo::after { content: ''; position: absolute; inset: 0; background: repeating-linear-gradient(135deg, rgba(255,255,255,0.05) 0 2px, transparent 2px 9px); }
        .lp-case-photo--after { background: linear-gradient(160deg, #2c2c33, #141417); }
        .lp-case-photo span { position: relative; z-index: 1; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; background: rgba(0,0,0,0.62); padding: 3px 7px; border-radius: 999px; margin: 8px; }
        .lp-case-stats { display: flex; justify-content: space-between; gap: 8px; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); padding: 12px 0; margin-bottom: 12px; }
        .lp-case-stats > div { text-align: center; flex: 1; }
        .lp-case-stats strong { display: block; font-size: 19px; font-weight: 800; letter-spacing: -0.02em; font-variant-numeric: tabular-nums; }
        .lp-case-stats span { font-size: 9px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--text-muted); }
        .lp-case-foot { font-size: 9px; text-transform: uppercase; letter-spacing: 0.14em; color: var(--text-muted); }

        @media (prefers-reduced-motion: reduce) {
          .rv { opacity: 1; transform: none; }
          .rv.rv-in { transition: none; }
          .lp-spot, .lp-spot:hover { transform: none; }
          .lp-particles, .lp-spin { display: none; }
          .lp-caret, .lp-typedots i { animation: none; }
        }
      `}</style>
    </div>
  );
};

export default Landing;
